import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ArrowDown,
  Phone,
  Clock,
  User,
  Eye,
  X,
  FileText,
  ArrowDownToLine,
  RefreshCw,
  PhoneIncoming,
  PhoneOutgoing
} from 'lucide-react';
import useAppStore from '../../store/appStore';
import {
  norm,
  toMMSS,
  toHHMMSS,
  parseDuration,
  getStatusBadgeClass,
  KPISkeleton,
  TableSkeleton,
  RefreshButton,
  PageHeader,
  SearchAndFilters,
  StatCard,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  CustomDropdown,
} from '../../lib/commonUtils';
import { useLanguage } from '../../context/LanguageContext';

const PAGE_SIZE = 20;

const DUMMY_CALL_HISTORY = [
  { id: 'd1', call_id: 'd1', timestamp: Date.now() - 1000 * 60 * 10, customer_name: 'Jean Dupont', customer_phone: '+33 6 12 34 56 78', agent_name: 'Agent Alpha', duration_seconds: 185, status: 'completed', type: 'inbound', summary: 'Customer inquired about subscription renewal. Issue resolved.' },
  { id: 'd2', call_id: 'd2', timestamp: Date.now() - 1000 * 60 * 45, customer_name: 'Marie Curie', customer_phone: '+33 7 98 76 54 32', agent_name: 'Agent Beta', duration_seconds: 92, status: 'completed', type: 'outbound', summary: 'Follow-up call for demo appointment confirmed for next Monday.' },
  { id: 'd3', call_id: 'd3', timestamp: Date.now() - 1000 * 60 * 120, customer_name: 'Pierre Martin', customer_phone: '+33 6 55 44 33 22', agent_name: 'Agent Alpha', duration_seconds: 0, status: 'failed', type: 'outbound', summary: '' },
  { id: 'd4', call_id: 'd4', timestamp: Date.now() - 1000 * 3600 * 3, customer_name: 'Sophie Bernard', customer_phone: '+33 7 11 22 33 44', agent_name: 'Agent Gamma', duration_seconds: 310, status: 'completed', type: 'inbound', summary: 'Billing concern addressed. Refund initiated.' },
  { id: 'd5', call_id: 'd5', timestamp: Date.now() - 1000 * 3600 * 5, customer_name: 'Lucas Moreau', customer_phone: '+33 6 77 66 55 44', agent_name: 'Agent Beta', duration_seconds: 54, status: 'completed', type: 'inbound', summary: 'Quick question about pricing tiers. Customer satisfied.' },
  { id: 'd6', call_id: 'd6', timestamp: Date.now() - 1000 * 3600 * 8, customer_name: 'Emma Leroy', customer_phone: '+33 7 33 22 11 00', agent_name: 'Agent Gamma', duration_seconds: 415, status: 'completed', type: 'outbound', summary: 'Onboarding walkthrough completed. Account setup confirmed.' },
  { id: 'd7', call_id: 'd7', timestamp: Date.now() - 1000 * 3600 * 24, customer_name: 'Thomas Roux', customer_phone: '+33 6 88 77 66 55', agent_name: 'Agent Alpha', duration_seconds: 0, status: 'missed', type: 'inbound', summary: '' },
  { id: 'd8', call_id: 'd8', timestamp: Date.now() - 1000 * 3600 * 26, customer_name: 'Camille Blanc', customer_phone: '+33 7 44 55 66 77', agent_name: 'Agent Beta', duration_seconds: 220, status: 'completed', type: 'inbound', summary: 'Technical support for API integration. Ticket created.' },
];

const CallLogs = () => {
  const { t, locale } = useLanguage();
  const { callHistory, fetchCallHistory, getAgentById } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callSummaries, setCallSummaries] = useState({});

  const doRefresh = useCallback(async () => {
    setIsLoading(true);
    try {
      await fetchCallHistory?.(50);
      setPage(1);
    } finally {
      setIsLoading(false);
    }
  }, [fetchCallHistory]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoading(true);
      try {
        await fetchCallHistory?.(50);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [fetchCallHistory]);

  const fetchCallSummary = useCallback((callId) => {
    if (callSummaries[callId]) return;
    const call = callHistory.find(c => c.id === callId || c.call_id === callId);
    setCallSummaries(prev => ({
      ...prev,
      [callId]: call?.summary || t('noSummaryAvailable'),
    }));
  }, [callSummaries, callHistory, t]);

  const handleViewSummary = useCallback((call) => {
    setSelectedCall(call);
    fetchCallSummary(call.id);
  }, [fetchCallSummary]);

  const closeSummaryModal = useCallback(() => {
    setSelectedCall(null);
  }, []);

  const rows = useMemo(() => {
    const realList = Array.isArray(callHistory) ? callHistory : [];
    return realList.map((r, idx) => {
      const v = r.timestamp ?? r.call_date ?? r.time ?? r.created_at ?? r.createdAt ?? r.start_time;
      const d = typeof v === 'number' ? new Date(v * (v < 2000000000 ? 1000 : 1)) : new Date(v);

      const agentName =
        r.agent_name ||
        getAgentById?.(r.agent_id)?.agent_name ||
        getAgentById?.(r.agent_id)?.name ||
        r.agent ||
        r.agent_id ||
        '—';

      return {
        id: r.id ?? r.call_id ?? r.sid ?? idx,
        timestamp: Number.isNaN(d.getTime()) ? null : d,
        customerName: r.customer_name ?? r.caller_name ?? r.customer ?? r.name ?? 'Unknown',
        customerPhone: r.customer_phone ?? r.phone_number ?? r.caller ?? r.phone ?? r.number ?? '',
        agent: agentName,
        durationLabel: parseDuration(r.duration_label ?? r.duration ?? r.duration_seconds),
        durationSeconds: Number(r.duration_seconds ?? r.duration ?? 0),
        status: r.status ?? r.call_status ?? 'completed',
        type: r.type ?? r.call_type ?? r.direction ?? 'inbound',
        summary: r.summary
      };
    });
  }, [callHistory, getAgentById]);

  const agentOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.agent && r.agent !== '—') set.add(r.agent); });
    return Array.from(set);
  }, [rows]);

  const filteredRows = useMemo(() => {
    const needle = norm(searchTerm);
    return rows.filter((r) => {
      const matchesSearch =
        norm(r.customerName).includes(needle) ||
        norm(r.customerPhone).includes(needle) ||
        norm(r.agent).includes(needle);

      const matchesStatus = filterStatus === 'all' || norm(r.status) === norm(filterStatus);
      const matchesAgent = filterAgent === 'all' || r.agent === filterAgent;

      return matchesSearch && matchesStatus && matchesAgent;
    });
  }, [rows, searchTerm, filterStatus, filterAgent]);

  useEffect(() => { setPage(1); }, [searchTerm, filterStatus, filterAgent]);

  const total = filteredRows.length;
  const totalCalls = total || rows.length;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const startIdx = (clampedPage - 1) * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, total);
  const pageRows = filteredRows.slice(startIdx, endIdx);

  const avgDurationSecs = useMemo(() => {
    const secList = rows.map((r) => Number(r.durationSeconds)).filter((n) => Number.isFinite(n) && n > 0);
    if (!secList.length) return null;
    const sum = secList.reduce((a, b) => a + b, 0);
    return Math.round(sum / secList.length);
  }, [rows]);
  const avgDurationLabel = avgDurationSecs == null ? '—' : toMMSS(avgDurationSecs);

  const totalDurationSecs = useMemo(() => {
    const secList = rows.map((r) => Number(r.durationSeconds)).filter((n) => Number.isFinite(n) && n > 0);
    if (!secList.length) return null;
    return secList.reduce((a, b) => a + b, 0);
  }, [rows]);
  const totalDurationLabel = totalDurationSecs == null ? '—' : toHHMMSS(totalDurationSecs);

  const successRate = useMemo(() => {
    if (!rows.length) return 0;
    const ok = rows.filter((r) => ['completed', 'succeeded', 'success'].includes(norm(r.status))).length;
    return Math.round((ok / rows.length) * 100);
  }, [rows]);

  const exportCsv = useCallback(() => {
    const header = ['Timestamp', 'Customer Name', 'Customer Phone', 'Agent', 'Duration', 'Status', 'Type', 'Summary'];
    const body = (filteredRows.length ? filteredRows : rows).map((r) => [
      r.timestamp ? `${r.timestamp.toLocaleDateString()} ${r.timestamp.toLocaleTimeString()}` : '',
      r.customerName || '',
      r.customerPhone || '',
      r.agent || '',
      r.durationLabel || '',
      r.status || '',
      r.type || '',
      callSummaries[r.id] || r.summary || 'Not loaded'
    ]);
    const rowsCsv = [header, ...body]
      .map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([rowsCsv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-logs-${new Date().getTime()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredRows, rows, callSummaries, t]);

  const clearSearch = () => setSearchTerm('');
  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterAgent('all');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader title={t('callLogs')} subtitle={t('viewCallsAndTranscripts')}>
        <div className="grid grid-cols-2 gap-3 w-full sm:w-auto mt-4 sm:mt-0">
          <RefreshButton onClick={doRefresh} isLoading={isLoading} className="btn-secondary w-full sm:w-auto" t={t} />
          <button onClick={exportCsv} className="btn btn-secondary w-full sm:w-auto">
            <ArrowDownToLine className="h-4 w-4" />
            <span>{t('exportCsv')}</span>
          </button>
        </div>
      </PageHeader>

      {isLoading ? (
        <KPISkeleton />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard title={t('totalCalls')} value={totalCalls} icon={Phone} />
          <StatCard title={t('avgCallDuration')} value={avgDurationLabel} icon={Clock} />
          <StatCard title={t('successRate')} value={successRate} format="percentage" icon={User} />
          <StatCard title={t('totalDuration')} value={totalDurationLabel} icon={Clock} />
        </div>
      )}

      <div className="lg:hidden p-4 controlled-glass rounded-[24px] mb-6 inner-highlight">
        <div className="space-y-4">
          <div className="relative group">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/20 group-focus-within:text-white transition-colors" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('searchCalls')}
              className="input pl-11"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CustomDropdown
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={[
                { value: 'all', label: t('allStatus') },
                { value: 'completed', label: t('completed') },
                { value: 'in_progress', label: t('running') },
                { value: 'missed', label: t('missed') },
              ]}
              placeholder={t('allStatus')}
              className="text-sm"
            />
            <CustomDropdown
              value={filterAgent}
              onChange={(e) => setFilterAgent(e.target.value)}
              options={[
                { value: 'all', label: t('allAgents') },
                ...agentOptions.map((a) => ({ value: a, label: a })),
              ]}
              placeholder={t('allAgents')}
              className="text-sm"
            />
          </div>
        </div>
      </div>

      <div className="hidden sm:block">
        <SearchAndFilters
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          searchPlaceholder={t('searchCalls')}
          t={t}
          filters={[
            {
              value: filterStatus,
              onChange: (e) => setFilterStatus(e.target.value),
              options: [
                { value: 'all', label: t('allStatus') },
                { value: 'completed', label: t('completed') },
                { value: 'in_progress', label: t('inProgress') },
                { value: 'missed', label: t('missed') },
              ],
            },
            {
              value: filterAgent,
              onChange: (e) => setFilterAgent(e.target.value),
              options: [
                { value: 'all', label: t('allAgents') },
                ...agentOptions.map((a) => ({ value: a, label: a })),
              ],
            },
          ]}
        />
      </div>

      {isLoading ? (
        <TableSkeleton rows={6} compactPad="px-6 py-4" />
      ) : filteredRows.length === 0 ? (
        <div className="controlled-glass p-12 rounded-[32px] inner-highlight">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-2xl bg-primary-500/10 border border-primary-500/20 p-5 mb-6">
              <Phone className="h-10 w-10 text-primary-400" />
            </div>
            <h3 className="text-xl font-bold text-white tracking-tight">{t('noCallHistory')}</h3>
            <p className="text-sm font-medium text-white/40 italic mt-2 max-w-md">
              {t('noCallsYetDesc')}
            </p>
            <div className="mt-8 flex items-center gap-3">
              <button onClick={clearSearch} className="btn py-2.5 px-6 rounded-xl bg-white/5 border border-white/10 text-xs font-bold uppercase tracking-widest hover:bg-white/10 transition-all">
                {t('clearSearch')}
              </button>
              <button onClick={resetFilters} className="btn py-2.5 px-6 rounded-xl bg-white/5 border border-white/10 text-xs font-bold uppercase tracking-widest hover:bg-white/10 transition-all">
                {t('resetFilters')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="glass-panel inner-highlight mb-6 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/5">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-5 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">{t('callInfo')}</th>
                    <th className="px-6 py-5 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">{t('customer')}</th>
                    <th className="px-6 py-5 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">{t('agent')}</th>
                    <th className="px-6 py-5 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">{t('duration')}</th>
                    <th className="px-6 py-5 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">{t('status')}</th>
                    <th className="px-6 py-5 sm:px-4 sm:py-2.5 text-left text-[10px] font-bold text-white/40 uppercase tracking-widest">{t('summaryLabel')}</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-white/5">
                  {pageRows.map((r) => (
                    <tr key={r.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-6 py-5 sm:px-4 sm:py-2.5">
                        <div className="text-sm font-bold text-white tracking-tight">
                          {r.timestamp ? r.timestamp.toLocaleDateString(locale) : '—'}
                        </div>
                        <div className="text-[11px] text-white/40 font-medium mt-0.5">
                          {r.timestamp ? r.timestamp.toLocaleTimeString(locale) : '—'}
                        </div>
                        <div className="text-[9px] font-bold uppercase tracking-widest flex items-center gap-1.5 mt-2 bg-white/5 w-fit px-2 py-0.5 rounded-full border border-white/5 text-white/40">
                          {norm(r.type) === 'inbound' ? (
                            <>
                              <PhoneIncoming className="h-3 w-3" />
                              <span>{t('inbound')}</span>
                            </>
                          ) : (
                            <>
                              <PhoneOutgoing className="h-3 w-3" />
                              <span>{t('outbound')}</span>
                            </>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-5 sm:px-4 sm:py-2.5">
                        <div className="text-sm font-bold text-white tracking-tight">{r.customerName}</div>
                        <div className="text-[11px] text-white/40 font-medium mt-0.5">{r.customerPhone}</div>
                      </td>

                      <td className="px-6 py-5 sm:px-4 sm:py-2.5">
                        <div className="text-sm font-bold text-white/70 tracking-tight">{r.agent}</div>
                      </td>

                      <td className="px-6 py-5 sm:px-4 sm:py-2.5">
                        <div className="text-sm font-bold text-white/70 tracking-tight">{r.durationLabel}</div>
                      </td>

                      <td className="px-6 py-5 sm:px-4 sm:py-2.5">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${getStatusBadgeClass(r.status)}`}>
                          {r.status}
                        </span>
                      </td>

                      <td className="px-6 py-5 sm:px-4 sm:py-2.5">
                        <button
                          onClick={() => handleViewSummary(r)}
                          className="btn py-2 px-4 sm:py-1 sm:px-3 rounded-xl sm:rounded-lg bg-primary-600 border border-primary-500 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-primary-500 transition-all shadow-lg shadow-primary-600/20"
                        >
                          <Eye className="h-3 w-3 mr-1.5 sm:scale-90" />
                          {t('view')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="controlled-glass rounded-[32px] p-5 sm:p-4 flex flex-col sm:flex-row items-center justify-between gap-4 inner-highlight mb-8">
            <p className="text-[11px] font-bold text-white/30 uppercase tracking-widest">
              {t('showing')} <span className="text-white/60">{total ? startIdx + 1 : 0}</span> {t('to')}{' '}
              <span className="text-white/60">{endIdx}</span> {t('of')}{' '}
              <span className="text-white/60">{total}</span> {t('results')}
            </p>
            <div className="flex items-center gap-2">
              <button
                className="btn py-2.5 px-5 sm:py-1.5 sm:px-3 text-[10px] font-bold uppercase tracking-widest rounded-xl sm:rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-20 transition-all"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={clampedPage <= 1}
              >
                {t('previous')}
              </button>
              <div className="w-10 h-10 sm:w-7 sm:h-7 rounded-xl sm:rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center text-xs font-bold text-primary-400">
                {clampedPage}
              </div>
              <button
                className="btn py-2.5 px-5 sm:py-1.5 sm:px-3 text-[10px] font-bold uppercase tracking-widest rounded-xl sm:rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-20 transition-all"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={clampedPage >= totalPages}
              >
                {t('next')}
              </button>
            </div>
          </div>
        </>
      )}
      {selectedCall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-slate-950/40 backdrop-blur-md"
            onClick={closeSummaryModal}
          />
          <div className="relative controlled-glass rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden inner-highlight">
            <div className="p-8 sm:p-6 border-b border-white/5 flex items-center justify-between bg-primary-500/5">
              <div className="flex items-center gap-4 sm:gap-3">
                <div className="p-3 sm:p-2.5 bg-primary-500/10 rounded-2xl border border-primary-500/20">
                  <FileText className="h-6 w-6 sm:h-5 sm:w-5 text-primary-400" />
                </div>
                <div>
                  <h3 className="text-2xl sm:text-xl font-bold text-white tracking-tight">{t('callSummary')}</h3>
                  <p className="text-xs font-bold text-white/30 uppercase tracking-widest mt-1 italic">{t('detailedBreakdown')}</p>
                </div>
              </div>
              <button
                onClick={closeSummaryModal}
                className="p-2.5 sm:p-2 bg-white/5 border border-white/10 rounded-xl transition-all text-white/40 hover:text-white"
              >
                <X className="h-6 w-6 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="p-10 sm:p-8 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <div className="bg-white/5 rounded-[32px] sm:rounded-[24px] p-8 sm:p-6 border border-white/5 inner-highlight">
                <p className="text-white/80 leading-relaxed text-lg sm:text-base italic font-medium">
                  "{callSummaries[selectedCall.id] || selectedCall.summary || t('noSummaryAvailable')}"
                </p>
              </div>
            </div>

            <div className="p-8 sm:p-6 bg-white/5 border-t border-white/5 flex justify-end">
              <button onClick={closeSummaryModal} className="btn py-3 px-8 sm:py-2 sm:px-6 rounded-xl bg-white/5 border border-white/10 text-xs font-bold uppercase tracking-widest hover:bg-white/10 transition-all">
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallLogs;